import AddIcon from "@mui/icons-material/Add";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives";

import { type GoalState } from "../buildRequestTemplate";

import styles from "./AddGoalMenu.module.css";

interface GoalTypeOption {
  goal_type: GoalState["goal_type"];
  label: string;
  description: string;
}

const GOAL_TYPE_OPTIONS: GoalTypeOption[] = [
  {
    goal_type: "dataset_summary",
    label: "Dataset summary",
    description:
      "The agent writes and persists a natural-language summary of the chosen dataset.",
  },
  {
    goal_type: "dataset_triage",
    label: "Dataset triage",
    description:
      "The agent picks zero or more labels from a vocabulary you define and applies them as tags.",
  },
  {
    goal_type: "create_events",
    label: "Create events",
    description:
      "The agent investigates the dataset, creates events on it, and files them into a collection.",
  },
];

interface AddGoalMenuProps {
  onAdd: (goal_type: GoalState["goal_type"]) => void;
  disabled?: boolean;
}

/**
 * Plus-button menu that picks the new goal's discriminator. Lives outside
 * the goal list so the user always has an obvious affordance to extend
 * the agent, even when zero goals are declared.
 */
export function AddGoalMenu({ onAdd, disabled }: AddGoalMenuProps) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // Close the menu if the trigger becomes disabled while open, so the popover
  // can't outlive its affordance.
  React.useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (event: MouseEvent) => {
      if (
        anchorRef.current !== null &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className={styles.anchor} ref={anchorRef}>
      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Add goal
      </Button>
      {open && (
        <div className={styles.popover} role="menu" aria-label="Add goal">
          {GOAL_TYPE_OPTIONS.map((option) => (
            <button
              key={option.goal_type}
              type="button"
              role="menuitem"
              className={styles.option}
              onClick={() => {
                onAdd(option.goal_type);
                setOpen(false);
              }}
            >
              <span className={styles.optionLabel}>{option.label}</span>
              <span className={styles.optionDescription}>
                {option.description}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
